import React from 'react';
import { Form, Input, Switch } from 'antd';
import {
  fieldNameMap,
  getWidgetType,
  multipleValueTypes,
  systemTypeKeyMap,
  wrapFieldValue,
} from '../utils';
import { requiredFieldList } from '@/components/DynamicForm/utils';
import { isArray, keys } from 'lodash';
import { MultipleSelect, Select, Text } from '@/components/DynamicForm/widget';
import { TEntryForm, TRequirementFieldConfig } from '@/components/DynamicForm/model';

const needDefaultWidget = [
  'select',
  'multiple_select',
  'radio',
  'checkbox',
  'member',
  'text',
  'textarea',
  'number',
  'progress',
  'percent',
  'cascade',
  'multiple_cascade',
];

export default function FieldConfigForm({
  requirement,
  selectedFieldId,
  fields,
  setFields,
}: {
  requirement?: TEntryForm;
  selectedFieldId?: string;
  fields: TRequirementFieldConfig[];
  setFields: (fields: TRequirementFieldConfig[]) => void;
}) {
  const onDescriptionChange = (event) => {
    const selectedFieldIndex = fields.findIndex(({ id }) => id === selectedFieldId);
    if (selectedFieldIndex < 0) {
      return;
    }
    const newFields = [...fields];
    newFields.splice(selectedFieldIndex, 1, {
      ...fields[selectedFieldIndex],
      description: event.target.value,
    });
    setFields(newFields);
  };

  const onRequiredChange = (checked) => {
    const selectedFieldIndex = fields.findIndex(({ id }) => id === selectedFieldId);
    if (selectedFieldIndex < 0) {
      return;
    }

    const newFields = [...fields];
    newFields.splice(selectedFieldIndex, 1, { ...fields[selectedFieldIndex], required: checked });
    setFields(newFields);
  };

  const onDefaultValueChange = (value, isEmpty) => {
    const selectedFieldIndex = fields.findIndex(({ id }) => id === selectedFieldId);
    if (selectedFieldIndex < 0) {
      return;
    }

    let newValue = [];
    // isEmpty 只用于富文本  不用于字段联动 {doChain: true}
    if (!value || (typeof isEmpty == 'boolean' && isEmpty)) {
      newValue = undefined;
    } else if (typeof value !== 'object') {
      newValue.push({
        name: value,
      });
    } else {
      if (typeof value === 'object' && !isArray(value)) {
        value = [value];
      }
      newValue = value.map((tempValue) => {
        if (typeof tempValue === 'object') {
          return tempValue;
        } else {
          return {
            name: tempValue,
          };
        }
      });
    }
    const newFields = [...fields];
    newFields.splice(selectedFieldIndex, 1, {
      ...fields[selectedFieldIndex],
      defaultValue: newValue,
    });
    setFields(newFields);
  };

  const setFieldsValue = (values) => {
    const field = fields.find(({ id }) => id === selectedFieldId);
    if (!field) {
      return;
    }
    keys(values).forEach((key) => {
      if (field.fieldType === key) {
        onDefaultValueChange(values[key]);
      }
    });
  };

  const systemTypeWidgetMap = {
    real_name: Text,
  };

  const defaultWidgetMap = {
    TEXT: Text,
    SELECT: Select,
    MULTIPLE_SELECT: MultipleSelect,
  };

  const renderDefaultValue = () => {
    if (!selectedFieldId) {
      return null;
    }

    const selectedFieldIndex = fields.findIndex(({ id }) => id === selectedFieldId);
    if (selectedFieldIndex < 0) {
      return;
    }

    const selectedField = fields[selectedFieldIndex];
    const { fieldType, defaultValue, customField } = selectedField;

    let Component = null;
    let multiple = null;
    const widgetType = getWidgetType(selectedField);
    if (fieldType !== 'custom') {
      const key = systemTypeKeyMap[fieldType] || fieldType;
      if (needDefaultWidget.includes(widgetType)) {
        Component = systemTypeWidgetMap[key];
      }
    } else {
      if (!customField) {
        return null;
      }

      if (!needDefaultWidget.includes(widgetType)) {
        return null;
      }

      if (widgetType) {
        Component = defaultWidgetMap[widgetType];
      }
      if (multipleValueTypes.includes(widgetType)) {
        multiple = true;
      }
    }

    if (!Component) {
      return null;
    }

    return (
      <Form.Item label="默认值">
        <Component
          requirement={requirement}
          onChange={onDefaultValueChange}
          choices={customField?.choices}
          value={wrapFieldValue(defaultValue, widgetType)}
          label={fieldNameMap[fieldType] || customField?.name}
          setFieldsValue={setFieldsValue}
          defaultValue={defaultValue}
          typeField={selectedField}
          colorEnabled={customField?.colorEnabled}
          fieldConfig
          disabled={false}
          customField={customField}
          multiple={multiple}
        />
      </Form.Item>
    );
  };

  const renderFormItems = () => {
    if (!selectedFieldId) {
      return null;
    }

    const selectedField = fields.find(({ id }) => id === selectedFieldId);

    if (!selectedField) {
      return null;
    }

    return (
      <>
        {!requiredFieldList.includes(selectedField.fieldType) && (
          <Form.Item label="是否必填：" className="required-form-item" layout="horizontal">
            <Switch
              key={selectedField.id}
              checked={selectedField.required}
              unCheckedChildren="否"
              checkedChildren="是"
              onChange={onRequiredChange}
            />
          </Form.Item>
        )}
        <Form.Item label="字段提示：" layout="vertical">
          <Input.TextArea
            onChange={onDescriptionChange}
            value={selectedField.description}
            placeholder="请输入..."
            count={{ max: 500, show: true }}
            maxLength={500}
            autoSize={{ minRows: 5 }}
          />
        </Form.Item>
        {renderDefaultValue()}
      </>
    );
  };

  return (
    <div className="field-config-form-wrapper" key={selectedFieldId}>
      <div className="field-config-form-header">字段设置</div>
      <div className="field-config-form-body">
        <Form className="field-config-form" layout="horizontal">
          {renderFormItems()}
        </Form>
      </div>
    </div>
  );
}
